import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AppLayout } from "../components/AppLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { api } from "../lib/api";
import { track, EVENTS } from "../lib/analytics";
import { useAuth } from "../lib/auth";
import { TestCallCard } from "../components/TestCallCard";
import { KinvantaCard } from "../components/KinvantaCard";
import { FeelingCard } from "../components/FeelingCard";
import {
  PhoneCall,
  CalendarClock,
  Smile,
  BellRing,
  TrendingUp,
  ArrowRight,
  Sparkles,
  Loader2,
  MessageSquareText,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const moodLabel = (s) => (s == null ? "—" : s >= 8 ? "Upbeat" : s >= 6 ? "Steady" : s >= 4 ? "Quiet" : "Low");
const moodColor = (s) =>
  s == null ? "bg-muted text-muted-foreground" :
  s >= 8 ? "bg-secondary/15 text-secondary" :
  s >= 6 ? "bg-primary/10 text-primary" :
  s >= 4 ? "bg-amber-100 text-amber-700" :
  "bg-rose-100 text-rose-700";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
    } catch (err) {
      toast.error("Couldn't load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const simulateCall = async () => {
    setSimulating(true);
    try {
      track(EVENTS.CALL_SIMULATED, { trigger: "manual" });
      const { data: res } = await api.post("/calls/simulate");
      toast.success("New call summary generated");
      const c = res?.call || {};
      track(EVENTS.CALL_COMPLETED, {
        trigger: "manual",
        mood_score: c.mood_score,
        sentiment: c.sentiment,
        duration_minutes: c.duration_minutes,
        status: c.status,
      });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't generate call");
    } finally {
      setSimulating(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="grid place-items-center h-96"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      </AppLayout>
    );
  }

  const { stats, recent_calls, loved_one, schedule, mood_trend, emotional, trend, alerts, plan } = data || {};
  const nextCallDate = stats?.next_call ? new Date(stats.next_call) : null;
  const isUnpaid = !plan || ["trial", "canceled"].includes(plan);

  return (
    <AppLayout
      title={loved_one ? `Hi, here's how ${loved_one.name} is doing today.` : "Welcome to CherishLink"}
      subtitle="Your peace-of-mind summary updated in real time."
      actions={
        <Button onClick={simulateCall} disabled={simulating} className="rounded-full" data-testid="simulate-call-button">
          {simulating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
          Generate new call
        </Button>
      }
    >
      {/* Test-call banner for unpaid/trial users — live demo call */}
      {isUnpaid && (
        <div className="mb-5">
          <TestCallCard
            variant="banner"
            source="dashboard"
            defaultPhone={user?.mobile || ""}
            defaultName={user?.name || ""}
            defaultEmail={user?.email || ""}
          />
        </div>
      )}

      {/* Stat cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          testid="stat-next-call"
          icon={CalendarClock} label="Next call scheduled"
          value={nextCallDate ? nextCallDate.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
          hint={schedule?.frequency === "daily" ? "Every day" : schedule?.frequency || "Not set"}
        />
        <StatCard
          testid="stat-calls-month"
          icon={PhoneCall} label="Calls this month"
          value={stats?.calls_this_month ?? 0}
          hint="Completed check-ins"
        />
        <StatCard
          testid="stat-last-mood"
          icon={Smile} label="Last mood"
          value={stats?.last_mood != null ? `${stats.last_mood}/10` : "—"}
          hint={moodLabel(stats?.last_mood)}
        />
        <StatCard
          testid="stat-alerts"
          icon={BellRing} label="Open alerts"
          value={stats?.alerts_open ?? 0}
          hint={stats?.alerts_open > 0 ? "Needs your attention" : "All quiet"}
          warn={stats?.alerts_open > 0}
        />
      </div>

      {/* Row 2: Emotional intelligence card (NEW) + loved one snapshot */}
      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        <div className="lg:col-span-2">
          <FeelingCard emotional={emotional} trend={trend} lovedOneName={loved_one?.name} />
        </div>

        <Card className="rounded-3xl border-border/60 soft-shadow">
          <CardContent className="p-6 md:p-8">
            <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Loved one</div>
            {loved_one ? (
              <>
                <div className="mt-3 flex items-center gap-3">
                  <div className="h-12 w-12 rounded-2xl bg-accent text-accent-foreground grid place-items-center font-display font-bold text-lg">
                    {loved_one.name?.[0]}
                  </div>
                  <div>
                    <div className="font-display text-xl font-semibold">{loved_one.name}</div>
                    <div className="text-xs text-muted-foreground">{loved_one.relationship} · {loved_one.phone}</div>
                  </div>
                </div>
                {loved_one.topics?.length > 0 && (
                  <div className="mt-5">
                    <div className="text-xs text-muted-foreground">Favorite topics</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {loved_one.topics.slice(0, 6).map((t) => (
                        <Badge key={t} variant="secondary" className="bg-accent text-accent-foreground rounded-full font-normal">{t}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {recent_calls?.[0]?.memory_notes && (
                  <div className="mt-5">
                    <div className="text-xs text-muted-foreground">Last notes</div>
                    <div className="text-sm mt-1 text-foreground/80 leading-relaxed">{recent_calls[0].memory_notes}</div>
                  </div>
                )}
                <Button asChild variant="ghost" className="mt-5 -ml-2" data-testid="loved-one-edit">
                  <Link to="/loved-one">Edit profile <ArrowRight className="h-4 w-4 ml-1" /></Link>
                </Button>
              </>
            ) : (
              <div className="mt-3 text-sm text-muted-foreground">
                <p>No loved one added yet.</p>
                <Button asChild className="mt-3 rounded-full"><Link to="/loved-one">Add now</Link></Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 2.5: Mood trend chart */}
      <div className="mt-6">
        <Card className="rounded-3xl border-border/60 soft-shadow">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Mood trend</div>
                <h3 className="mt-1 font-display text-xl font-semibold">Last {mood_trend?.length || 0} calls</h3>
              </div>
              <TrendingUp className="h-5 w-5 text-secondary" />
            </div>
            <div className="mt-6 h-56 min-h-[224px] w-full" data-testid="mood-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={mood_trend || []} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis domain={[0, 10]} stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                  <Line type="monotone" dataKey="mood" stroke="hsl(var(--primary))" strokeWidth={3} dot={{ r: 4, fill: "hsl(var(--primary))" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 3: recent calls */}
      <Card className="rounded-3xl border-border/60 soft-shadow mt-6" data-testid="recent-calls">
        <CardContent className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Recent calls</div>
              <h3 className="mt-1 font-display text-xl font-semibold">Latest check-ins</h3>
            </div>
          </div>
          {recent_calls?.length > 0 ? (
            <div className="overflow-x-auto -mx-2">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Date</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Mood</TableHead>
                    <TableHead className="min-w-[300px]">Summary</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recent_calls.map((c) => (
                    <TableRow key={c.id} data-testid={`call-row-${c.id}`}>
                      <TableCell className="whitespace-nowrap">
                        {new Date(c.started_at).toLocaleDateString([], { month: "short", day: "numeric" })}
                      </TableCell>
                      <TableCell>{c.duration_minutes} min</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${moodColor(c.mood_score)}`}>
                          {c.mood_score}/10 · {moodLabel(c.mood_score)}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-foreground/80 max-w-xl">{c.summary}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="bg-secondary/15 text-secondary rounded-full font-normal capitalize">
                          {c.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="p-10 text-center text-muted-foreground">
              <MessageSquareText className="h-8 w-8 mx-auto text-muted-foreground/50" />
              <p className="mt-3">No calls yet. Generate your first to see CherishLink in action.</p>
              <Button onClick={simulateCall} className="mt-4 rounded-full" disabled={simulating} data-testid="empty-simulate">
                {simulating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate a call"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Row 4: alerts + upgrade */}
      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        {alerts?.length > 0 && (
          <Card className="lg:col-span-2 rounded-3xl border-amber-200 bg-amber-50/40 soft-shadow">
            <CardContent className="p-6 md:p-8">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-2xl bg-amber-100 grid place-items-center shrink-0">
                  <BellRing className="h-5 w-5 text-amber-700" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-amber-900">{alerts[0].title}</div>
                  <p className="mt-2 text-sm text-amber-900/80 leading-relaxed">{alerts[0].message}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {plan !== "family" && (
          <Card className="rounded-3xl border-border/60 bg-foreground text-background soft-shadow lg:col-span-1">
            <CardContent className="p-6 md:p-8">
              <div className="text-sm font-semibold uppercase tracking-wider opacity-70">Upgrade</div>
              <h3 className="mt-1 font-display text-xl font-semibold">Get daily calls + smart alerts</h3>
              <p className="mt-2 text-sm text-white/70">
                Plus adds daily warm conversations, SMS alerts, and richer memory continuity.
              </p>
              <Button asChild className="mt-4 rounded-full bg-white text-foreground hover:bg-white/90" data-testid="upgrade-cta">
                <Link to="/billing">Upgrade plan <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Kinvanta sister-product side card */}
      <div className="mt-6 max-w-md">
        <KinvantaCard variant="sidecard" source="dashboard" />
      </div>
    </AppLayout>
  );
}

function StatCard({ icon: Icon, label, value, hint, warn, testid }) {
  return (
    <Card className={`rounded-3xl soft-shadow hover-lift ${warn ? "border-amber-200 bg-amber-50/40" : "border-border/60"}`} data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</div>
            <div className="mt-2 font-display text-2xl font-bold tracking-tight">{value}</div>
            {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
          </div>
          <div className={`h-10 w-10 rounded-2xl grid place-items-center ${warn ? "bg-amber-100" : "bg-accent"}`}>
            <Icon className={`h-5 w-5 ${warn ? "text-amber-700" : "text-primary"}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
